import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { IconAlertCircle, IconCheck, IconInfoCircle } from '@tabler/icons-react'

const AUTO_HIDE_MS = 2000
const ANIMATION_MS = 250

const ToastContext = createContext(null)

function normalizeToastInput(input) {
  if (typeof input === 'string') {
    return { text: input, type: 'success' }
  }

  return {
    text: input.text,
    type: input.type ?? 'success',
  }
}

function ToastIcon({ type }) {
  if (type === 'error') {
    return <IconAlertCircle size={18} stroke={2} className="shrink-0 text-red-500" />
  }

  if (type === 'info') {
    return <IconInfoCircle size={18} stroke={2} className="shrink-0 text-accent" />
  }

  return <IconCheck size={18} stroke={2} className="shrink-0 text-accent" />
}

function ToastItem({ toast, onDone }) {
  const [phase, setPhase] = useState('entering')
  const doneRef = useRef(false)

  useEffect(() => {
    const showTimer = window.setTimeout(() => {
      setPhase('visible')
    }, 0)

    const hideTimer = window.setTimeout(() => {
      setPhase('leaving')
    }, AUTO_HIDE_MS)

    return () => {
      window.clearTimeout(showTimer)
      window.clearTimeout(hideTimer)
    }
  }, [])

  const handleTransitionEnd = useCallback((event) => {
    if (event.propertyName !== 'transform' || phase !== 'leaving' || doneRef.current) {
      return
    }

    doneRef.current = true
    onDone()
  }, [onDone, phase])

  const phaseClass =
    phase === 'visible'
      ? 'app-toast--visible'
      : phase === 'leaving'
        ? 'app-toast--leaving'
        : ''

  return (
    <div
      role="status"
      aria-live="polite"
      className={`app-toast pointer-events-auto flex max-w-sm items-center gap-2.5 rounded-xl bg-[var(--color-card-elevated)] px-4 py-2.5 shadow-lg shadow-black/10 ${phaseClass}`}
      onTransitionEnd={handleTransitionEnd}
    >
      <ToastIcon type={toast.type} />
      <span className="text-[14px] font-medium leading-snug text-[var(--color-text)]">
        {toast.text}
      </span>
    </div>
  )
}

function ToastOverlay({ toast, toastKey, onDone }) {
  if (!toast) return null

  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-[200] flex justify-center px-4"
      style={{ paddingTop: 'max(12px, var(--app-content-top))' }}
    >
      <ToastItem key={toastKey} toast={toast} onDone={onDone} />
    </div>,
    document.body,
  )
}

export function ToastProvider({ children }) {
  const [toast, setToast] = useState(null)
  const [toastKey, setToastKey] = useState(0)

  const hideToast = useCallback(() => {
    setToast(null)
  }, [])

  const showToast = useCallback((input) => {
    setToast(normalizeToastInput(input))
    setToastKey((current) => current + 1)
  }, [])

  const contextValue = useMemo(() => ({ showToast }), [showToast])

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastOverlay toast={toast} toastKey={toastKey} onDone={hideToast} />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)

  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }

  return context
}

export { AUTO_HIDE_MS, ANIMATION_MS }
